import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { SERVICE_OPTIONS } from "@/content/site";
import { submitDiagnostic } from "@/lib/api";

const schema = z.object({
  full_name: z.string().min(2, "الرجاء إدخال الاسم الكامل"),
  organization: z.string().min(2, "الرجاء إدخال اسم الجهة"),
  job_title: z.string().optional().or(z.literal("")),
  email: z.string().email("بريد إلكتروني غير صحيح"),
  mobile: z.string().min(6, "رقم جوال غير صحيح"),
  service_path: z.string().min(1, "اختر مسار التأثير"),
  service_other: z.string().optional().or(z.literal("")),
  challenge: z.string().min(5, "صف التحدي الذي تريد معالجته"),
  privacy_consent: z.literal(true, { errorMap: () => ({ message: "يجب الموافقة على سياسة الخصوصية" }) }),
  newsletter_opt_in: z.boolean().optional(),
}).refine(
  (data) => data.service_path !== "other" || (data.service_other && data.service_other.trim().length >= 2),
  { message: "الرجاء وصف المسار الآخر", path: ["service_other"] },
);

const fieldBase = "w-full bg-white border border-navy/20 rounded-none px-4 py-3 text-navy focus-visible:ring-2 focus-visible:ring-offset-0 outline-none";

export default function DiagnosticForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [serverError, setServerError] = useState("");
  const preService = params.get("service") || "";

  const hidden = useMemo(() => ({
    service: preService,
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    referrer: typeof document !== "undefined" ? document.referrer : "",
    landing_path: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
  }), [params, preService]);

  const {
    register, handleSubmit, setValue, watch, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "", organization: "", job_title: "", email: "", mobile: "",
      service_path: SERVICE_OPTIONS.some((o) => o.value === preService) ? preService : "",
      service_other: "",
      challenge: "", privacy_consent: false, newsletter_opt_in: false,
    },
  });

  useEffect(() => {
    if (SERVICE_OPTIONS.some((o) => o.value === preService)) setValue("service_path", preService);
  }, [preService, setValue]);

  const consent = watch("privacy_consent");
  const newsletter = watch("newsletter_opt_in");
  const servicePath = watch("service_path");

  const onSubmit = async (data) => {
    setServerError("");
    try {
      const res = await submitDiagnostic({ ...data, ...hidden, company_website: "" });
      if (res?.status === "ok") {
        navigate("/thank-you");
      } else {
        setServerError("تعذّر إرسال الطلب. حاول مجددًا.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setServerError(typeof msg === "string" ? msg : "حدث خطأ أثناء الإرسال. تأكد من البيانات وحاول مجددًا.");
    }
  };

  const Err = ({ name }) => errors[name] ? (
    <p className="mt-1.5 text-sm flex items-center gap-1.5" style={{ color: "var(--coral)" }} role="alert"><AlertCircle size={14} />{errors[name].message}</p>
  ) : null;

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="diagnostic-form" className="space-y-6">
      {/* Honeypot */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
        <label htmlFor="company_website">الموقع الإلكتروني</label>
        <input id="company_website" type="text" tabIndex={-1} autoComplete="off" name="company_website" onChange={() => {}} />
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="full_name" className="block mb-2 font-medium text-navy">الاسم الكامل <span style={{ color: "var(--coral)" }}>*</span></label>
          <input id="full_name" autoComplete="name" className={fieldBase} data-testid="form-full-name" {...register("full_name")} />
          <Err name="full_name" />
        </div>
        <div>
          <label htmlFor="organization" className="block mb-2 font-medium text-navy">الجهة <span style={{ color: "var(--coral)" }}>*</span></label>
          <input id="organization" autoComplete="organization" className={fieldBase} data-testid="form-organization" {...register("organization")} />
          <Err name="organization" />
        </div>
        <div>
          <label htmlFor="job_title" className="block mb-2 font-medium text-navy">المسمّى الوظيفي</label>
          <input id="job_title" autoComplete="organization-title" className={fieldBase} data-testid="form-job-title" {...register("job_title")} />
        </div>
        <div>
          <label htmlFor="email" className="block mb-2 font-medium text-navy">البريد الإلكتروني <span style={{ color: "var(--coral)" }}>*</span></label>
          <input id="email" type="email" autoComplete="email" dir="ltr" className={`${fieldBase} text-right`} data-testid="form-email" {...register("email")} />
          <Err name="email" />
        </div>
        <div>
          <label htmlFor="mobile" className="block mb-2 font-medium text-navy">الجوال <span style={{ color: "var(--coral)" }}>*</span></label>
          <input id="mobile" type="tel" autoComplete="tel" dir="ltr" className={`${fieldBase} text-right`} data-testid="form-mobile" {...register("mobile")} />
          <Err name="mobile" />
        </div>
        <div>
          <label htmlFor="service_path" className="block mb-2 font-medium text-navy">مسار التأثير <span style={{ color: "var(--coral)" }}>*</span></label>
          <select id="service_path" className={fieldBase} data-testid="form-service-path" {...register("service_path")}>
            <option value="">اختر المسار</option>
            {SERVICE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <Err name="service_path" />
        </div>
      </div>

      {servicePath === "other" && (
        <div data-testid="service-other-wrap">
          <label htmlFor="service_other" className="block mb-2 font-medium text-navy">
            صف المسار الذي تحتاجه <span style={{ color: "var(--coral)" }}>*</span>
          </label>
          <textarea
            id="service_other"
            rows={3}
            placeholder="اكتب باختصار المسار أو نوع الدعم الذي تحتاجه…"
            className={fieldBase}
            data-testid="form-service-other"
            {...register("service_other")}
          />
          <Err name="service_other" />
        </div>
      )}

      <div>
        <label htmlFor="challenge" className="block mb-2 font-medium text-navy">ما التحدي الذي تريد معالجته؟ <span style={{ color: "var(--coral)" }}>*</span></label>
        <textarea id="challenge" rows={5} className={fieldBase} data-testid="form-challenge" {...register("challenge")} />
        <Err name="challenge" />
      </div>

      <div className="space-y-4 pt-2">
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1.5 h-4 w-4 accent-[var(--coral)]" data-testid="form-consent" {...register("privacy_consent")} />
          <span className="text-navy leading-relaxed">أوافق على <a href="/privacy" className="link-underline" style={{ color: "var(--coral)" }}>سياسة الخصوصية</a> ومعالجة بياناتي لغرض التواصل. <span style={{ color: "var(--coral)" }}>*</span></span>
        </label>
        <Err name="privacy_consent" />

        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" className="mt-1.5 h-4 w-4 accent-[var(--coral)]" data-testid="form-newsletter" {...register("newsletter_opt_in")} />
          <span className="text-navy leading-relaxed">أرغب في الاشتراك بالنشرة المعرفية (اختياري).</span>
        </label>
      </div>

      {serverError && (
        <div className="border p-4 flex items-center gap-2" style={{ borderColor: "var(--coral)", color: "var(--coral)" }} role="alert" data-testid="form-server-error">
          <AlertCircle size={18} />{serverError}
        </div>
      )}

      <button type="submit" disabled={isSubmitting} data-testid="form-submit" className="group inline-flex items-center gap-2 bg-coral text-white px-8 py-4 text-base font-semibold hover:bg-[#d85a5e] transition-colors disabled:opacity-60" style={{ backgroundColor: "var(--coral)" }}>
        {isSubmitting ? (<><Loader2 size={18} className="animate-spin" /> جارٍ الإرسال…</>) : (<>أرسل الطلب <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" /></>)}
      </button>

      {/* keep unused watch refs to avoid lint noise */}
      <span className="sr-only">{consent ? "" : ""}{newsletter ? "" : ""}</span>
    </form>
  );
}
